import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  ProgramApiService,
  Enrollment,
  LastSessionDetails,
  NextWorkout,
  ProgramSession,
  ProgressionHistory,
} from '../../services/api/ProgramApiService';
import { NextWorkoutCard } from './NextWorkoutCard';
import { LogProgramSessionDialog } from './LogProgramSessionDialog';
import { ProgramProgressChart } from './ProgramProgressChart';
import { SessionHistoryList } from './SessionHistoryList';
import { EditSessionDialog } from './EditSessionDialog';
import { EditWeightsDialog } from './EditWeightsDialog';
import { ActiveWorkoutView } from './ActiveWorkoutView';
import { useActiveWorkout } from '../../hooks/useActiveWorkout';

interface ActiveEnrollmentPanelProps {
  enrollment: Enrollment;
  onEnrollmentChanged: () => void;
}

export const ActiveEnrollmentPanel: React.FC<ActiveEnrollmentPanelProps> = ({
  enrollment,
  onEnrollmentChanged,
}) => {
  const { t } = useLanguage();
  const [nextWorkout, setNextWorkout] = useState<NextWorkout | null>(null);
  const [sessions, setSessions] = useState<ProgramSession[]>([]);
  const [progression, setProgression] = useState<ProgressionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [actionAlert, setActionAlert] = useState<string | null>(null);

  // Edit/delete last session
  const [lastSession, setLastSession] = useState<LastSessionDetails | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Edit starting weights
  const [editWeightsOpen, setEditWeightsOpen] = useState(false);

  // Active workout tracking
  const { activeWorkout, isInProgress, startWorkout } = useActiveWorkout();
  const [activeWorkoutOpen, setActiveWorkoutOpen] = useState(false);
  const thisEnrollmentActive = isInProgress && activeWorkout?.enrollmentId === enrollment.id;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [next, hist, prog] = await Promise.all([
        ProgramApiService.getNextWorkout(enrollment.id),
        ProgramApiService.getSessionHistory(enrollment.id),
        ProgramApiService.getProgressionHistory(enrollment.id),
      ]);
      setNextWorkout(next);
      setSessions(hist);
      setProgression(prog);
    } catch {
      setError('Failed to load enrollment data');
    } finally {
      setLoading(false);
    }
  }, [enrollment.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSessionLogged = async () => {
    setActionAlert(t('programs.sessionLogged'));
    setTimeout(() => setActionAlert(null), 4000);
    await load();
    onEnrollmentChanged();
  };

  const handleEditLast = async () => {
    try {
      const data = await ProgramApiService.getLastSession(enrollment.id);
      if (!data) return;
      setLastSession(data);
      setEditDialogOpen(true);
    } catch (err: any) {
      setError(err.message ?? 'Failed to fetch last session');
    }
  };

  const handleDeleteConfirmed = async () => {
    setDeleteConfirmOpen(false);
    try {
      await ProgramApiService.deleteLastSession(enrollment.id);
      setActionAlert(t('programs.sessionDeleted'));
      setTimeout(() => setActionAlert(null), 4000);
      await load();
      onEnrollmentChanged();
    } catch (err: any) {
      setError(err.message ?? 'Failed to delete session');
    }
  };

  const handlePauseResume = async () => {
    try {
      await ProgramApiService.updateEnrollment(
        enrollment.id,
        enrollment.status === 'active' ? 'paused' : 'active'
      );
      onEnrollmentChanged();
    } catch (err: any) {
      setError(err.message ?? 'Failed to update status');
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(t('programs.leaveConfirm'))) return;
    try {
      await ProgramApiService.leaveProgram(enrollment.id);
      onEnrollmentChanged();
    } catch (err: any) {
      setError(err.message ?? 'Failed to leave program');
    }
  };

  const statusLabel = t(`programs.status.${enrollment.status}` as any);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h6">{enrollment.program_name}</Typography>
        <Chip size="small" label={statusLabel} color={enrollment.status === 'active' ? 'success' : 'default'} />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {t('programs.sessionsCompleted')}: {enrollment.sessions_completed}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: enrollment.sessions_completed === 0 ? 0.5 : 2 }}>
        {t('programs.enrolledSince')}: {new Date(enrollment.enrolled_at).toLocaleDateString()}
      </Typography>

      {enrollment.sessions_completed === 0 && (
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => setEditWeightsOpen(true)}
          sx={{ mb: 2 }}
        >
          {t('programs.editWeights')}
        </Button>
      )}

      {actionAlert && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {actionAlert}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Next workout / in-progress */}
          {enrollment.status === 'active' && (
            <Box sx={{ mb: 2 }}>
              {thisEnrollmentActive ? (
                <Alert
                  severity="info"
                  icon={<ScheduleIcon />}
                  action={
                    <Button size="small" onClick={() => setActiveWorkoutOpen(true)}>
                      {t('programs.resumeWorkout')}
                    </Button>
                  }
                >
                  <Typography variant="body2">
                    {t('programs.workoutInProgress')} — {activeWorkout!.templateName}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {t('programs.startedAt')}{' '}
                    {new Date(activeWorkout!.startedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Alert>
              ) : nextWorkout ? (
                <>
                  <NextWorkoutCard nextWorkout={nextWorkout} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        startWorkout(enrollment.id, nextWorkout);
                        setActiveWorkoutOpen(true);
                      }}
                    >
                      {t('programs.startWorkout')}
                    </Button>
                    <Button variant="outlined" onClick={() => setLogDialogOpen(true)}>
                      {t('programs.logManual')}
                    </Button>
                  </Box>
                </>
              ) : null}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Tabs: chart / history */}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={t('programs.progressChart')} />
            <Tab label={t('programs.sessionHistory')} />
          </Tabs>

          {tab === 0 && <ProgramProgressChart history={progression} />}
          {tab === 1 && (
            <SessionHistoryList
              sessions={sessions}
              onEditLast={sessions.length > 0 ? handleEditLast : undefined}
              onDeleteLast={sessions.length > 0 ? () => setDeleteConfirmOpen(true) : undefined}
            />
          )}

          <Divider sx={{ my: 2 }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={handlePauseResume}>
              {enrollment.status === 'active' ? t('programs.pause') : t('programs.resume')}
            </Button>
            <Button variant="outlined" size="small" color="error" onClick={handleLeave}>
              {t('programs.leave')}
            </Button>
          </Box>
        </>
      )}

      {/* Log session dialog */}
      {nextWorkout && logDialogOpen && (
        <LogProgramSessionDialog
          open={logDialogOpen}
          enrollmentId={enrollment.id}
          nextWorkout={nextWorkout}
          onClose={() => setLogDialogOpen(false)}
          onSessionLogged={handleSessionLogged}
        />
      )}

      {/* Edit session dialog */}
      {lastSession && editDialogOpen && (
        <EditSessionDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          enrollmentId={enrollment.id}
          lastSession={lastSession}
          onSessionEdited={async () => {
            setActionAlert(t('programs.sessionUpdated'));
            setTimeout(() => setActionAlert(null), 4000);
            await load();
            onEnrollmentChanged();
          }}
        />
      )}

      {/* Delete last session confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('programs.deleteSession')}</DialogTitle>
        <DialogContent>
          <Typography>{t('programs.deleteSessionConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirmed}>
            {t('programs.deleteSession')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit starting weights dialog */}
      {editWeightsOpen && (
        <EditWeightsDialog
          open={editWeightsOpen}
          onClose={() => setEditWeightsOpen(false)}
          enrollmentId={enrollment.id}
          exerciseStates={enrollment.exercise_states}
          onWeightsUpdated={async () => {
            setActionAlert(t('programs.weightsUpdated'));
            setTimeout(() => setActionAlert(null), 4000);
            await load();
            onEnrollmentChanged();
          }}
        />
      )}

      {/* Active workout tracker */}
      <ActiveWorkoutView
        open={activeWorkoutOpen}
        onClose={() => setActiveWorkoutOpen(false)}
        onSessionLogged={handleSessionLogged}
      />
    </Box>
  );
};
